"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "UserPlusIcon",
    title: "Create Your Account",
    description: "Register with your organization details, county, and contact information. Verify your email and mobile number to activate your account.",
    detail: "Takes about 3 minutes",
    color: "kenya-red",
    href: "/sign-up",
    cta: "Create Account",
  },
  {
    number: "02",
    icon: "DocumentPlusIcon",
    title: "Submit Your Product",
    description: "Complete the structured submission form with your product details, location, high-quality images, and compliance declarations.",
    detail: "Guided 5-step form",
    color: "kenya-black",
    href: "/product-submission",
    cta: "View Form",
  },
  {
    number: "03",
    icon: "CheckBadgeIcon",
    title: "Get Reviewed & Marketed",
    description: "Ministry experts review your submission. Approved products receive a tracking number, official profile sheet, and national marketing exposure.",
    detail: "7–14 business days",
    color: "kenya-green",
    href: "/sign-up",
    cta: "Get Started",
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".step-item");
            items.forEach((item, i) => {
              setTimeout(() => item.classList.add("revealed"), i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-warm-surface relative overflow-hidden">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 sm:mb-24 step-item reveal-hidden">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-kenya-green/10 border border-kenya-green/20 mb-6">
            <div className="w-2 h-2 rounded-full bg-kenya-green" />
            <span className="text-[0.6875rem] font-bold uppercase tracking-[0.4em] text-kenya-green font-condensed">
              How It Works
            </span>
          </div>
          <h2
            className="font-display font-bold text-kenya-black mb-4 sm:mb-5"
            style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)", lineHeight: "0.92", letterSpacing: "-0.025em" }}
          >
            From Submission to
            <br />
            <span className="font-display italic font-light text-kenya-green">Global Visibility</span>
          </h2>
          <p className="text-text-muted max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            A simple, guided three-step process designed for first-time users and experienced tourism professionals alike.
          </p>
        </div>

        {/* Steps */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-16 left-[16.67%] right-[16.67%] h-[2px] bg-gradient-to-r from-kenya-red via-kenya-black to-kenya-green opacity-20" />

          {steps.map((step, idx) => (
            <div
              key={step.number}
              className="step-item reveal-hidden relative"
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              {/* Step card */}
              <div className="group p-7 sm:p-10 rounded-xl3 bg-white border-2 border-kenya-black/8 hover:shadow-card-hover transition-all duration-500">
                {/* Number + icon */}
                <div className="flex items-start justify-between mb-6 sm:mb-8">
                  <div
                    className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      step.color === "kenya-red" ?"bg-kenya-red/10"
                        : step.color === "kenya-green" ?"bg-kenya-green/10" :"bg-kenya-black/8"
                    } group-hover:scale-110 transition-transform`}
                  >
                    <Icon
                      name={step.icon as Parameters<typeof Icon>[0]["name"]}
                      size={28}
                      className={
                        step.color === "kenya-red" ?"text-kenya-red"
                          : step.color === "kenya-green" ?"text-kenya-green" :"text-kenya-black"
                      }
                    />
                  </div>
                  <span
                    className={`font-display font-bold text-5xl ${
                      step.color === "kenya-red" ?"text-kenya-red/15"
                        : step.color === "kenya-green" ?"text-kenya-green/15" :"text-kenya-black/10"
                    }`}
                  >
                    {step.number}
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-bold text-kenya-black mb-3 sm:mb-4 font-display">
                  {step.title}
                </h3>
                <p className="text-text-muted text-[0.875rem] sm:text-[0.9375rem] leading-relaxed mb-5 sm:mb-6">
                  {step.description}
                </p>

                {/* Detail tag */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="ClockIcon" size={14} className="text-text-muted" />
                    <span className="text-[0.8125rem] text-text-muted font-medium">{step.detail}</span>
                  </div>
                  <Link
                    href={step.href}
                    className={`text-[0.8125rem] font-bold uppercase tracking-[0.15em] flex items-center gap-1.5 group/link font-condensed ${
                      step.color === "kenya-red" ?"text-kenya-red"
                        : step.color === "kenya-green" ?"text-kenya-green" :"text-kenya-black"
                    }`}
                  >
                    {step.cta}
                    <Icon name="ArrowRightIcon" size={13} className="group-hover/link:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 sm:mt-16 step-item reveal-hidden">
          <Link
            href="/sign-up"
            className="inline-flex items-center gap-3 bg-kenya-red text-white px-7 sm:px-10 py-4 sm:py-5 rounded-xl font-bold text-sm sm:text-base tracking-[0.05em] hover:bg-kenya-red/90 transition-all shadow-kenya"
          >
            Start Your Submission Today
            <Icon name="ArrowRightIcon" size={17} />
          </Link>
          <p className="mt-3 sm:mt-4 text-text-muted text-[0.8125rem] sm:text-[0.875rem]">
            Free to submit · No upfront costs · Ministry-backed marketing
          </p>
        </div>
      </div>
    </section>
  );
}